import {
  forwardRef,
  type AnchorHTMLAttributes,
  type ButtonHTMLAttributes,
  type HTMLAttributes,
} from 'react'
import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

type ActionVariant = 'primary' | 'secondary' | 'ghost' | 'danger'
type ActionSize = 'sm' | 'md' | 'lg'

const actionBase =
  'inline-flex items-center justify-center gap-2 rounded-lg font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50'

const actionVariants: Record<ActionVariant, string> = {
  primary: 'bg-indigo-600 text-white hover:bg-indigo-700',
  secondary:
    'border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700',
  ghost: 'text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-300 dark:hover:bg-slate-700 dark:hover:text-white',
  danger: 'bg-red-600 text-white hover:bg-red-700',
}

const actionSizes: Record<ActionSize, string> = {
  sm: 'min-h-8 px-3 py-1.5 text-xs',
  md: 'min-h-10 px-4 py-2 text-sm',
  lg: 'min-h-12 px-6 py-3 text-base',
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ActionVariant
  size?: ActionSize
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, size = 'md', type = 'button', variant = 'primary', ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cn(actionBase, actionVariants[variant], actionSizes[size], className)}
      {...props}
    />
  )
})

export interface ActionLinkProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  variant?: ActionVariant
  size?: ActionSize
}

export const ActionLink = forwardRef<HTMLAnchorElement, ActionLinkProps>(function ActionLink(
  { className, size = 'md', variant = 'primary', ...props },
  ref,
) {
  return (
    <a
      ref={ref}
      className={cn(actionBase, actionVariants[variant], actionSizes[size], className)}
      {...props}
    />
  )
})

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  size?: 'sm' | 'md'
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { className, size = 'md', type = 'button', ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cn(
        'inline-flex items-center justify-center rounded-lg text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-white',
        size === 'sm' ? 'h-8 w-8' : 'h-10 w-10',
        className,
      )}
      {...props}
    />
  )
})

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  tone?: 'default' | 'muted' | 'accent'
}

export const Card = forwardRef<HTMLDivElement, CardProps>(function Card(
  { className, tone = 'default', ...props },
  ref,
) {
  return (
    <div
      ref={ref}
      className={cn(
        'rounded-xl border',
        tone === 'default' &&
          'border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800',
        tone === 'muted' &&
          'border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800/60',
        tone === 'accent' &&
          'border-indigo-200 bg-indigo-50 dark:border-indigo-500/40 dark:bg-indigo-500/10',
        className,
      )}
      {...props}
    />
  )
})

export function SectionLabel({ className, ...props }: HTMLAttributes<HTMLHeadingElement>) {
  return (
    <h3
      className={cn(
        'text-xs font-bold uppercase tracking-[0.14em] text-slate-400',
        className,
      )}
      {...props}
    />
  )
}

export function StatusDot({
  className,
  tone = 'neutral',
  ...props
}: HTMLAttributes<HTMLSpanElement> & {
  tone?: 'success' | 'warning' | 'danger' | 'info' | 'neutral'
}) {
  const toneClass = {
    success: 'bg-emerald-500',
    warning: 'bg-amber-500',
    danger: 'bg-red-500',
    info: 'bg-indigo-500',
    neutral: 'bg-slate-400',
  }[tone]

  return (
    <span
      className={cn('inline-block h-2 w-2 shrink-0 rounded-full', toneClass, className)}
      {...props}
    />
  )
}
